import {observer_mgl} from "mobx-graphlink";
import moment from "moment";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, Select, Text, TextArea, TextInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {ScrollView} from "react-vscrollview";
import {HSLA, RunInAction_Set} from "web-vcore";
import {StoryExploreComp} from "../../../Engine/LiveSession/Components/StoryExploreComp.js";
import {GetLiveSession_Reactive} from "../../../Engine/LiveSessionManagement.js";
import {AddStoryMessage, DeleteStoryMessage, UpdateStoryMessage} from "../../../Server/Commands/StoryMessageCommands.js";
import {Story} from "../../../Server/DB/stories.js";
import {FindMessagesInChain} from "../../../Server/DB/storyMessages.js";
import {StoryMessage} from "../../../Server/DB/storyMessages/@StoryMessage.js";
import {store} from "../../../Store/index.js";
import {ShowSummarizeRangeDialog} from "./SummarizeRangeDialog.js";
import {MDIcon} from "../../../Utils/UI/MDIcon.js";

function GetTitleForOffChainState(onMainChain: boolean, onVerboseChain: boolean) {
	if (onMainChain) return undefined; // messages on the main-chain stay uncolored
	return [
		!onMainChain && !onVerboseChain && `Red because message is on neither the "main chain" nor "verbose chain".`,
		!onMainChain && onVerboseChain && `Orange because message is not on the "main chain". (but *is* on the "verbose chain")`,
		`The "verbose chain" is made up of:`,
		`* The last non-summary message, and its chain of active ancestors.`,
		`The "main chain" is the same, EXCEPT that, for it:`,
		`* The "chain of ancestors" gets "redirected" by summarization messages.`,
		`(ie. if verbose-chain is [1,2,3], but active summary '9' summarizes 1+2, resulting main-chain = [9,3])`,
	].filter(a=>a).join("\n");
}
function GetStyleForOffChainState(onMainChain: boolean, onVerboseChain: boolean) {
	if (!onMainChain && !onVerboseChain) return {background: "hsla(0,100%,30%,.8)"};
	if (!onMainChain && onVerboseChain) return {background: "hsla(30,100%,35%,.8)"};
	return undefined;
}

class MessageStub extends BaseComponent<{story: Story, messages: StoryMessage[], id: string, idTrimLength?: number, mainChain?: StoryMessage[], verboseChain?: StoryMessage[]}, {}> {
	render() {
		const {story, messages, id, idTrimLength, mainChain, verboseChain} = this.props;
		const message = messages.find(a=>a.id == id);
		if (message == null) return <Text>{id} (missing)</Text>;
		const onMainChain = mainChain != null && mainChain.includes(message);
		const onVerboseChain = verboseChain != null && verboseChain.includes(message);

		return (
			<Button p="0 5px" title={GetTitleForOffChainState(onMainChain, onVerboseChain)} style={GetStyleForOffChainState(onMainChain, onVerboseChain)} text={message.id.slice(0, idTrimLength ?? 5)} onClick={()=>{
				ShowMessageBox({
					title: "Message details",
					message: ()=>{
						return <ScrollView style={{maxHeight: window.innerHeight - 100}}>
							<StoryMessageUI story={story} message={message} messages={messages} mainChain={mainChain} verboseChain={verboseChain}/>
						</ScrollView>;
					},
				});
			}}/>
		);
	}
}

export const StoryMessageUI = observer_mgl((props: {story: Story, message: StoryMessage, messages: StoryMessage[], messagesShown?: StoryMessage[], mainChain?: StoryMessage[], verboseChain?: StoryMessage[]})=>{
	const {story, message, messages, messagesShown, mainChain, verboseChain} = props;
	const index = messages.indexOf(message);
	const liveSession = GetLiveSession_Reactive();
	const storyExploreComp = liveSession?.Comp(StoryExploreComp);
	let msgStubCallCount = 0;
	const msgStub = (id: string|n, idTrimLength?: number)=>{
		if (id == null) return <Text key={`msgStub_none_${msgStubCallCount++}`}>none</Text>;
		return <MessageStub key={id} story={story} messages={messages} id={id} idTrimLength={idTrimLength} mainChain={mainChain} verboseChain={verboseChain}/>;
	};

	const childMessages = messages.filter(a=>a.parent == message.id);

	/*const UpdateMessage = (changerFunc: (data: JournalSegment)=>any)=>{
		const segments_new = Clone(entry.segments);
		const segment_newData = segments_new[index];
		changerFunc(segment_newData);
		new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
	};*/

	const uiState = store.main.content;
	const [editMode, setEditMode] = useState(false);
	const [showChildren, setShowChildren] = useState(false);

	//const hiddenAncestorChainLength = hiddenAncestorChainLengths?.get(message) ?? 0;
	let hiddenAncestorChain = [] as StoryMessage[];
	//let hiddenAncestorChainLength_noSummaries = 0; // could add this as extra info; not yet since requires care to not cause slowdown
	if (messagesShown) {
		const hiddenAncestorChain_raw = FindMessagesInChain(messages, message, true, msg=>msg != message && messagesShown.includes(msg), false);
		// we have to filter out the start-message and terminate-at message (which may well be visible)
		hiddenAncestorChain = hiddenAncestorChain_raw.filter(a=>!messagesShown.includes(a));
	}
	const hiddenAncestorsUI = hiddenAncestorChain.length > 0 &&
		<Row center mt={index == 0 ? 0 : 3} pl={5} style={{
			background: `rgba(255,255,0,.4)`, borderRadius: 5, flexWrap: "wrap", gap: 3,
		}}>
			<Text>{`${hiddenAncestorChain.length} hidden ancestors (some may themselves be summaries). List:`}</Text>
			{hiddenAncestorChain.length > 0 && hiddenAncestorChain.map(a=>msgStub(a.id, 2))}
		</Row>;

	const onMainChain = mainChain != null && mainChain.includes(message);
	const onVerboseChain = verboseChain != null && verboseChain.includes(message);
	// note: these three things are only non-null / apply if the message is off-chain
	const offChainHoverText = GetTitleForOffChainState(onMainChain, onVerboseChain);
	const offChainStyle_idLabel = GetStyleForOffChainState(onMainChain, onVerboseChain);
	//const offChainStyle_leftBorder = offChainStyle_idLabel ? {borderLeft: `5px solid ${offChainStyle_idLabel.background}`} : null;
	const offChain_leftBorderUI = offChainStyle_idLabel == null ? ()=>null : ()=>{
		return <div title={offChainHoverText} style={{
			position: "absolute", right: "100%", top: 0, bottom: 0, width: 5, background: offChainStyle_idLabel.background
		}}/>;
	};
	return (
		<>
			{hiddenAncestorsUI}
			<Column style={{position: "relative"}}>
				{offChain_leftBorderUI()}
				<Row center mt={index == 0 && hiddenAncestorsUI == null ? 0 : 3} pl={5} style={{
					background: HSLA(0, 0, 1, .3), borderRadius: "5px 5px 0 0", flexWrap: "wrap", gap: 5,
				}}>
					<Row sel style={{flexWrap: "wrap", gap: 5}}>
						<Text title={offChainHoverText} style={offChainStyle_idLabel}>
							ID: {message.id}
						</Text>
						{!editMode && <Text>Parent: {msgStub(message.parent)}</Text>}
						{!editMode && message.parent != null && (()=>{
							const summaryIntercepts = messages.filter(a=>a.summary_chainEnd == message.parent && a.summary_enabled);
							return summaryIntercepts.length > 0 && (
								<Row style={{gap: 3}}>
									<Text>Summaries for parent:</Text>
									{summaryIntercepts.map(a=><React.Fragment key={a.id}>{msgStub(a.id)}</React.Fragment>)}
								</Row>
							);
						})()}
						{editMode && <>
							<Text>Parent:</Text>
							<TextInput style={{width: 200}} value={message.parent} onChange={val=>{
								if (val.trim().length > 0 && messages.find(a=>a.id == val) == null) {
									return void ShowMessageBox({title: "Invalid parent id", message: "No message with that id exists."});
								}
								new UpdateStoryMessage({id: message.id, updates: {parent: val}}).RunOnServer();
							}}/>
						</>}
						{!editMode && <Text>Role: {message.role}</Text>}
						{editMode && <>
							<Text>Role:</Text>
							<Select options={["user", "assistant"]} value={message.role} onChange={val=>{
								new UpdateStoryMessage({id: message.id, updates: {role: val}}).RunOnServer();
							}}/>
						</>}
						{childMessages.length > 0 &&
						<Row>
							<Text>Children: {childMessages.length}</Text>
							<Button p="0 5px" text={showChildren ? "hide" : "show"} onClick={()=>setShowChildren(!showChildren)}/>
							{showChildren && childMessages.map(a=>msgStub(a.id))}
						</Row>}
						<Text>Created at: {moment(message.createdAt).format("YYYY-MM-DD HH:mm:ss")}</Text>
						{!editMode && message.model != null && <Text>Model: {message.model}</Text>}
						{editMode && <>
							<Text>Model:</Text>
							<TextInput style={{width: 200}} value={message.model ?? ""} onChange={val=>{
								new UpdateStoryMessage({id: message.id, updates: {model: val.trim().length > 0 ? val.trim() : null}}).RunOnServer();
							}}/>
						</>}
						{(message.summary_chainEnd || editMode) && <>
							{!editMode && <Text>Summary chain end: {msgStub(message.summary_chainEnd)}</Text>}
							{editMode && <>
								<Text>Summary chain end:</Text>
								<TextInput style={{width: 200}} value={message.summary_chainEnd} onChange={val=>{
									if (val.trim().length > 0) {
										if (messages.find(a=>a.id == val) == null) {
											// we merely warn here, because it may be that we have a summarizing-message, but have already deleted the messages it summarizes (which we want to support)
											ShowMessageBox({title: "Warning: Invalid summary chain end id", message: "No message with that id exists."});
										}
										new UpdateStoryMessage({id: message.id, updates: {summary_chainEnd: val}}).RunOnServer();
									} else {
										new UpdateStoryMessage({id: message.id, updates: {summary_chainEnd: null}}).RunOnServer();
									}
								}}/>
							</>}
							<CheckBox text="Summary enabled" value={message.summary_enabled ?? false} onChange={val=>{
								new UpdateStoryMessage({id: message.id, updates: {summary_enabled: val}}).RunOnServer();
							}}/>
							<Button text="Recreate summary" onClick={()=>{
								const chainEnd = messages.find(a=>a.id == message.summary_chainEnd);
								// traverse chain from summary_chainEnd back to the first message in summarized set (ie. message whose parent = the summary's parent)
								const summarizedChain = chainEnd ? FindMessagesInChain(messages, chainEnd, true, a=>a.parent == message.parent) : [];
								const firstMessageInChain = summarizedChain.length > 0 ? summarizedChain[0] : undefined;
								ShowSummarizeRangeDialog({
									story, messages,
									messagesToSummarize_firstId: firstMessageInChain?.id,
									messagesToSummarize_lastId: message.summary_chainEnd ?? undefined,
									afterCreate_customStep: async(_newSummaryId, _firstMessage, _lastMessage)=>{
										await new UpdateStoryMessage({id: message.id, updates: {summary_enabled: false}}).RunOnServer();
									},
									afterCreate_uiAddition: ()=>(
										<Row mt={10} style={{color: "orange", fontWeight: "bold"}}>Note: Old summary also marked as disabled.</Row>
									),
								});
							}}/>
						</>}
					</Row>
					<Row ml="auto">
						<CheckBox ml={5} text="Edit" value={editMode} onChange={val=>setEditMode(val)}/>
						<Text ml={5}>Mark (1-2):</Text>
						<CheckBox text="" value={uiState.mark_1 == message.id} onChange={val=>RunInAction_Set(()=>uiState.mark_1 = val ? message.id : null)}/>
						<CheckBox text="" value={uiState.mark_2 == message.id} onChange={val=>RunInAction_Set(()=>uiState.mark_2 = val ? message.id : null)}/>
						<Button ml={5} p="5px 10px" style={{alignSelf: "center"}}
							text={<>
								{message.content.length}
								<MDIcon ml={5} icon="information" size={18}/>
							</>}
							onClick={()=>{
								ShowMessageBox({
									title: "Message details",
									message: ()=><Text sel style={{whiteSpace: "pre-wrap"}}>{[
										`Characters: ${message.content.length}`,
										`Words: ${message.content.match(/\S+/g)?.length ?? 0}`,
										`Lines: ${message.content.split("\n").length}`,
										message.summary_chainEnd && (()=>{
											const chainEnd = messages.find(a=>a.id == message.summary_chainEnd);
											const summarizedChain = chainEnd ? FindMessagesInChain(messages, chainEnd, true, a=>a.parent == message.parent) : [];
											const summarizedText = summarizedChain.map(a=>a.content).join("\n");
											const summarizedChars = summarizedText.length;
											const summarizedWords = summarizedText.match(/\S+/g)?.length ?? 0;
											const summarizedLines = summarizedText.split("\n").length;
											return `Summarizes: ${summarizedChain.length} messages (${summarizedChars} chars, ${summarizedWords} words, ${summarizedLines} lines)`;
										})(),
									].filter(a=>a).join("\n")}</Text>,
								});
							}}/>
						<Button ml={5} text="Resume" enabled={storyExploreComp?.behaviorEnabled} style={{padding: "5px 12px", fontSize: 12}} onClick={()=>{
							ShowMessageBox({
								title: "Resume story?", cancelButton: true,
								message: "This will replace the current story in the live session. Continue?",
								onOK: ()=>{
									storyExploreComp?.LoadStoryForChain(story, messages, message);
								},
							});
						}}/>
						<Button ml={5} size={32} mdIcon="content-copy" onClick={()=>{
							ShowMessageBox({
								title: "Clone message?", cancelButton: true,
								message: "Clone this message, as a new peer? (ie. with same parent)",
								onOK: ()=>{
									const newMessage = new StoryMessage({
										role: message.role,
										content: message.content,
										model: message.model,
										story: message.story,
										parent: message.parent,
									});
									new AddStoryMessage({entry: newMessage}).RunOnServer();
								},
							});
						}}/>
						<Button ml={5} size={32} mdIcon="delete" enabled={childMessages.length == 0} onClick={()=>{
							ShowMessageBox({
								title: "Delete message?", cancelButton: true,
								message: "Permanently delete this message of the story?",
								onOK: ()=>{
									new DeleteStoryMessage({id: message.id}).RunOnServer();
								},
							});
						}}/>
					</Row>
				</Row>
				<Row>
					<TextArea style={{flex: 70, border: "1px solid black"}} autoSize={true} enabled={editMode} value={message.content} onChange={val=>{
						new UpdateStoryMessage({id: message.id, updates: {content: val}}).RunOnServer();
					}}/>
				</Row>
			</Column>
		</>
	);
});